/* Notification Center — API layer (2026-09-21, RTCFR).
 *
 * Backs the previously in-memory rules with real persistence
 * (dbo.AI_NotificationCategory + dbo.AI_NotificationRule via
 * NotificationRulesController). The store hydrates from getAll() on load and
 * write-throughs every mutation here, so new/edited rules survive an API
 * restart. Maps the API DTOs ↔ the frontend NcCategory/NcRule shapes. */
import { api } from '../../api/client';
import { groupMembersDisplay, groupChannels } from './data';
import type { NcCategory, NcRule, NcTemplate, NcRecipientGroup, NcRecipient, NcGroupUser, NcDelivery, NcRuleGroupRef, DeliveryStatus, DeliveryAction, Purpose, Severity } from './data';
import type { PlPermit } from './permitLibrary';
import type { HoldTimeCatalogRow, NcHoldTimeTarget } from './holdTime';

interface RuleDto {
  code: string; categoryCode: number; name: string; sortOrder: number; purpose: string;
  severity?: string | null; outbound: boolean; externalRecipient: boolean; ack: boolean; enabled: boolean;
  targets?: string | null; recipients: string; triggers: string[]; targetKeys: string[]; config?: string | null;
  /** Recipient groups by stable id. Read: live name + exists. Write: only `id` is
   *  used; omitted (undefined) = legacy name-based fallback on the server. */
  recipientGroups?: NcRuleGroupRef[] | null;
  /** Linked Email Template id (a reference, never a copy); null = none. */
  emailTemplateId?: string | null;
  /** Pop-up setting for this rule's in-app alerts. Read: null = follows the Default.
   *  Write: null/absent = keep the stored link; the empty Guid = clear it (follow the Default). */
  popupSettingId?: string | null;
  /** Delivery Method ticks. Read: always set. Write: null = keep the stored value. */
  notifyInApp?: boolean | null;
  notifyEmail?: boolean | null;
  /** Delivery Method → Auto-resolve. Read: always set. Write: null = keep the stored value. */
  autoResolve?: boolean | null;
  /** Delivery Method → Delay / Business hours / Bypass digest. Read: always set. Write: null = keep. */
  delayMode?: string | null;
  businessHoursOnly?: boolean | null;
  bypassDigest?: boolean | null;
  /** Repeat until acknowledged. Read: always set. Write: null = keep. */
  repeatEnabled?: boolean | null;
  repeatEvery?: number | null;
  repeatUnit?: string | null;
  /** Escalation. Read: null = none. Write: null = keep; the empty Guid = clear. */
  escalationGroupId?: string | null;
  escalateAfter?: string | null;
  /** Hold Time targets. Read: the stored set (null for other rules). Write: null /
   *  absent = leave the stored set untouched; a list replaces it. */
  holdTimeTargets?: NcHoldTimeTarget[] | null;
}
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
interface CategoryDto { code: number; codeLabel?: string; name: string; icon: string; order: number; active: boolean; rules: RuleDto[] }

function toRule(d: RuleDto): NcRule {
  return {
    name: d.name, code: d.code, purpose: d.purpose as Purpose,
    severity: (d.severity ?? undefined) as Severity | undefined,
    outbound: d.outbound || undefined,
    targets: d.targets ?? undefined,
    triggers: d.triggers?.length ? d.triggers : undefined,
    targetKeys: d.targetKeys?.length ? d.targetKeys : undefined,
    sortOrder: d.sortOrder,
    config: d.config ?? undefined,
    recipients: d.recipients,
    externalRecipient: d.externalRecipient || undefined,
    ack: d.ack || undefined,
    enabled: d.enabled,
    recipientGroups: d.recipientGroups ?? [],
    emailTemplateId: d.emailTemplateId ?? null,
    popupSettingId: d.popupSettingId ?? null,
    notifyInApp: d.notifyInApp ?? false,
    notifyEmail: d.notifyEmail ?? true,
    autoResolve: d.autoResolve ?? false,
    delayMode: d.delayMode === 'After1Hour' || d.delayMode === 'DailyDigest' ? d.delayMode : 'Immediate',
    businessHoursOnly: d.businessHoursOnly ?? false,
    bypassDigest: d.bypassDigest ?? true,
    repeatEnabled: d.repeatEnabled ?? false,
    repeatEvery: d.repeatEvery ?? 4,
    repeatUnit: d.repeatUnit === 'Minutes' ? 'Minutes' : 'Hours',
    escalationGroupId: d.escalationGroupId ?? null,
    escalateAfter: d.escalateAfter === '30m' || d.escalateAfter === '4h' || d.escalateAfter === 'EOD' ? d.escalateAfter : '1h',
    holdTimeTargets: d.holdTimeTargets?.length
      // Each warning comes back as the value + unit the user chose (an old hour-only value
      // reads back as Hours); the server's derived `minutes` is not kept on the client.
      ? d.holdTimeTargets.map((t) => ({ ...t, warnings: (t.warnings ?? []).map((w) => ({ value: Number(w.value), unit: w.unit })) }))
      : undefined,
  };
}
function toCategory(d: CategoryDto): NcCategory {
  return { code: d.code, codeLabel: d.codeLabel ?? '', name: d.name, icon: d.icon, order: d.order, active: d.active, rules: (d.rules ?? []).map(toRule) };
}
function ruleToDto(categoryCode: number, r: NcRule): RuleDto {
  return {
    code: r.code, categoryCode, name: r.name, sortOrder: r.sortOrder ?? 0, purpose: r.purpose,
    severity: r.severity ?? null, outbound: !!r.outbound, externalRecipient: !!r.externalRecipient,
    ack: !!r.ack, enabled: r.enabled, targets: r.targets ?? null, recipients: r.recipients,
    triggers: r.triggers ?? [], targetKeys: r.targetKeys ?? [], config: r.config ?? null,
    recipientGroups: r.recipientGroups,
    emailTemplateId: r.emailTemplateId ?? null,
    // null/undefined = untouched (keep the stored link, so a stale page can never wipe an assignment
    // made elsewhere); '' = the user explicitly chose "follow the Default" → the empty Guid clears it.
    popupSettingId: r.popupSettingId === '' ? EMPTY_GUID : (r.popupSettingId ?? null),
    notifyInApp: r.notifyInApp ?? null,
    notifyEmail: r.notifyEmail ?? null,
    autoResolve: r.autoResolve ?? null,
    delayMode: r.delayMode ?? null,
    businessHoursOnly: r.businessHoursOnly ?? null,
    bypassDigest: r.bypassDigest ?? null,
    repeatEnabled: r.repeatEnabled ?? null,
    repeatEvery: r.repeatEvery ?? null,
    repeatUnit: r.repeatUnit ?? null,
    // '' = the user chose "No escalation" → the empty Guid clears it; null/undefined = untouched.
    escalationGroupId: r.escalationGroupId === '' ? EMPTY_GUID : (r.escalationGroupId ?? null),
    escalateAfter: r.escalateAfter ?? null,
    // Only the write fields, and only when the rule carries them (Hold Time) —
    // every other rule sends null, which the server treats as "untouched".
    holdTimeTargets: r.holdTimeTargets
      ? r.holdTimeTargets.map((t) => ({ testMethodId: t.testMethodId, holdTimeType: t.holdTimeType, enabled: t.enabled,
          warnings: t.warnings.map((w) => ({ value: w.value, unit: w.unit })) }))
      : null,
  };
}

/** The whole categories+rules tree from the DB (store hydrates from this). */
export async function getAll(): Promise<NcCategory[]> {
  const res = await api.get('/api/v1/notification-rules');
  const data = (res.data?.data ?? []) as CategoryDto[];
  return data.map(toCategory);
}
/** Create or update one rule (matched on Code). */
export async function upsertRule(categoryCode: number, rule: NcRule): Promise<void> {
  await api.post('/api/v1/notification-rules/rule', ruleToDto(categoryCode, rule));
}
/** Soft-delete rules by Code. */
export async function deleteRules(codes: string[]): Promise<void> {
  await api.post('/api/v1/notification-rules/rule/delete', { codes });
}
/** Create (code 0 — the SERVER mints the next RC-NN Category Code, race-safe) or
 *  update (matched on code, which never changes) one category. Returns the saved
 *  category's code + Category Code label. */
export async function upsertCategory(cat: NcCategory): Promise<{ code: number; codeLabel: string }> {
  const res = await api.post('/api/v1/notification-rules/category', {
    code: cat.code, name: cat.name, icon: cat.icon, order: cat.order, active: cat.active, rules: [],
  });
  const data = res.data?.data as { code: number; codeLabel: string } | undefined;
  return { code: data?.code ?? cat.code, codeLabel: data?.codeLabel ?? cat.codeLabel ?? '' };
}
/** Soft-delete categories (and their rules) by Code. */
export async function deleteCategories(codes: number[]): Promise<void> {
  await api.post('/api/v1/notification-rules/category/delete', { codes });
}

interface TemplateDto {
  id?: string | null;
  name: string; category?: string | null; subject: string; body?: string | null;
  active: boolean; sortOrder: number; matchName?: string | null;
}
function toTemplate(d: TemplateDto): NcTemplate {
  return { id: d.id ?? undefined, name: d.name, category: d.category ?? '', subject: d.subject, body: d.body ?? undefined, active: d.active, sortOrder: d.sortOrder };
}
/** Every Email Template from the DB (store hydrates the Email Templates list). */
export async function getTemplates(): Promise<NcTemplate[]> {
  const res = await api.get('/api/v1/notification-rules/templates');
  const data = (res.data?.data ?? []) as TemplateDto[];
  return data.map(toTemplate);
}
/** Create or update one template and return its stable id. An existing template
 *  is located by `tpl.id` (a rename keeps the same id, so every rule linked via
 *  EmailTemplateId follows the new name); `prevName` (its current name) is only
 *  the fallback for a template without an id. `sortOrder` orders the list. */
export async function upsertTemplate(prevName: string, tpl: NcTemplate, sortOrder: number): Promise<string> {
  const res = await api.post('/api/v1/notification-rules/template', {
    id: tpl.id ?? null, name: tpl.name, category: tpl.category || null, subject: tpl.subject,
    body: tpl.body ?? null, active: tpl.active, sortOrder, matchName: prevName,
  });
  return res.data?.data?.id as string;
}
/** Soft-delete templates by Name. */
export async function deleteTemplates(names: string[]): Promise<void> {
  await api.post('/api/v1/notification-rules/template/delete', { names });
}

interface GroupMemberDto { role: string; department?: string | null; notifyInApp: boolean; notifyEmail: boolean; notifySMS: boolean }
/** An individually selected user. Read: live name / roles / department + exists.
 *  Write: only userId + the three channel flags are used by the server. */
interface GroupUserDto {
  userId: string; userName?: string | null; name?: string | null; roles?: string | null; department?: string | null;
  notifyInApp: boolean; notifyEmail: boolean; notifySMS: boolean; exists?: boolean;
}
interface GroupDto { id: string; name: string; active: boolean; usedBy: number; members: GroupMemberDto[]; users?: GroupUserDto[] | null }
function toGroup(d: GroupDto): NcRecipientGroup {
  const recipients: NcRecipient[] = (d.members ?? []).map((m, i) => ({
    id: `m-${i}-${m.role}`, name: m.role, kind: 'role',
    department: m.department ?? undefined,
    inApp: m.notifyInApp, email: m.notifyEmail, sms: m.notifySMS,
  }));
  const users: NcGroupUser[] = (d.users ?? []).map((u) => ({
    userId: u.userId, userName: u.userName ?? '', name: u.name ?? undefined, roles: u.roles ?? undefined, department: u.department ?? undefined,
    inApp: u.notifyInApp, email: u.notifyEmail, sms: u.notifySMS, exists: u.exists ?? true,
  }));
  return {
    id: d.id, name: d.name, members: groupMembersDisplay(recipients, users) || '—',
    channels: groupChannels(recipients, users), usedBy: d.usedBy, recipients, users,
  };
}
/** Every Recipient Group (with its members) from the DB. */
export async function getRecipientGroups(): Promise<NcRecipientGroup[]> {
  const res = await api.get('/api/v1/notification-rules/recipient-groups');
  const data = (res.data?.data ?? []) as GroupDto[];
  return data.map(toGroup);
}
/** Create or update one group + its members and return its stable id. An
 *  existing group is located by `group.id` (so a rename keeps the same id and
 *  every rule linked to it follows the new name); `prevName` (its current name)
 *  is only the fallback for a group without an id. */
export async function upsertRecipientGroup(prevName: string, group: NcRecipientGroup): Promise<string> {
  const members: GroupMemberDto[] = (group.recipients ?? []).map((r) => ({
    role: r.name, department: r.department ?? null,
    notifyInApp: r.inApp, notifyEmail: r.email, notifySMS: !!r.sms,
  }));
  // Users go by their existing id only (the server re-reads name/roles/department).
  // Omitted when the group has no `users` array at all, so an untouched list is kept.
  const users = group.users?.map((u) => ({
    userId: u.userId, notifyInApp: u.inApp, notifyEmail: u.email, notifySMS: u.sms,
  }));
  const res = await api.post('/api/v1/notification-rules/recipient-group', {
    id: group.id ?? null, name: group.name, active: true, usedBy: group.usedBy, matchName: prevName, members,
    ...(users ? { users } : {}),
  });
  return res.data?.data?.id as string;
}
/** Soft-delete recipient groups by Name (removes their members). */
export async function deleteRecipientGroups(names: string[]): Promise<void> {
  await api.post('/api/v1/notification-rules/recipient-group/delete', { names });
}

interface DeliveryDto {
  sentAt: string; ruleName?: string | null; subject?: string | null; channel?: string | null;
  role?: string | null; recipient?: string | null; status: string; detail?: string | null;
  id?: string; action?: string | null; actionNote?: string | null;
  attemptType?: string | null; attemptedByName?: string | null; originalSentAt?: string | null;
  ackRequired?: boolean | null; acknowledgedBy?: string | null; acknowledgedAt?: string | null;
  resolvedAt?: string | null;
}
interface DeliveryPageDto {
  items: DeliveryDto[]; total: number; failed: number; last24h: number; failed24h: number; page: number; pageSize: number;
}
export interface DeliveryPage {
  items: NcDelivery[]; total: number; failed: number; last24h: number; failed24h: number; page: number; pageSize: number;
}
/** One page of the Delivery Log — REAL persisted records (dbo.AI_NotificationDeliveryLog). */
export async function getDeliveries(page: number, pageSize: number): Promise<DeliveryPage> {
  const res = await api.get('/api/v1/notification-rules/deliveries', { params: { page, pageSize } });
  const d = (res.data?.data ?? {}) as Partial<DeliveryPageDto>;
  return {
    items: (d.items ?? []).map((x): NcDelivery => ({
      time: x.sentAt, rule: x.ruleName ?? '', subject: x.subject ?? '', channel: x.channel ?? '',
      role: x.role ?? '', recipient: x.recipient ?? '', status: (x.status as DeliveryStatus) || 'Sent', detail: x.detail ?? '',
      id: x.id, action: x.action === 'Resend' || x.action === 'SendAgain' ? x.action : null,
      actionNote: x.actionNote ?? undefined, attemptType: x.attemptType ?? undefined,
      attemptedBy: x.attemptedByName ?? undefined, originalTime: x.originalSentAt ?? undefined,
      ackRequired: x.ackRequired ?? undefined, acknowledgedBy: x.acknowledgedBy ?? undefined,
      acknowledgedAt: x.acknowledgedAt ?? undefined, resolvedAt: x.resolvedAt ?? undefined,
    })),
    total: d.total ?? 0, failed: d.failed ?? 0, last24h: d.last24h ?? 0, failed24h: d.failed24h ?? 0,
    page: d.page ?? page, pageSize: d.pageSize ?? pageSize,
  };
}

/** What a Resend / Send Again attempt did: the NEW Delivery Log row's status
 *  (Sent · Failed · Skipped) and the transport's detail (failure reason or test-mode note). */
export interface ResendResult { status: string; detail?: string | null; recipient?: string | null }

/** Delivery Log → Resend / Send Again one email. Only the row id travels: the server
 *  re-reads the original recipient, subject and body, sends through the configured SMTP
 *  (honouring test-mode redirection) and logs the attempt as a NEW row. Rejects with the
 *  server's message (403 · 404 · 409). */
export async function resendDelivery(id: string, kind: DeliveryAction): Promise<ResendResult> {
  const res = await api.post(`/api/v1/notification-rules/deliveries/${encodeURIComponent(id)}/resend`, { kind });
  return res.data?.data as ResendResult;
}

/** The API's own error message from a failed call (the standard envelope), else a fallback. */
export function apiErrorText(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { error?: { message?: unknown } } } })?.response?.data?.error?.message;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
}

/** One distinct Role + Department pair from the Users data source, plus the
 *  comma-joined names of the users in that Role + Department. */
export interface RecipientRole { role: string; department?: string | null; users?: string | null }

/** The full live Permit Library (permits + limit rows) for the Targets grid. */
interface PermitLimitDto {
  parameter: string; synonym?: string | null; limitType: string; operator: string;
  limit: string; limit2?: string | null; units: string; effectiveFrom?: string | null; effectiveTo?: string | null;
}
interface PermitLibraryDto {
  permitId: string; permitName: string; permitType: string; sampleMatrix: string; active: boolean; limits: PermitLimitDto[];
}
/** Load the FULL Permit Library (every configured parameter) — the source of
 *  truth for the Targets grid. Mapped to the PlPermit shape buildPermitTargets
 *  consumes, so both the Rule Detail Targets tab and the wizard Target step read
 *  the same real data. */
export async function getPermitLibrary(): Promise<PlPermit[]> {
  const res = await api.get('/api/v1/notification-rules/permit-library');
  const data = (res.data?.data ?? []) as PermitLibraryDto[];
  return data.map((p) => ({
    permitId: p.permitId, permitName: p.permitName, permitType: p.permitType,
    sampleMatrix: p.sampleMatrix, active: p.active,
    limits: (p.limits ?? []).map((l) => ({
      parameter: l.parameter, synonym: l.synonym ?? undefined, limitType: l.limitType, operator: l.operator,
      limit: l.limit, limit2: l.limit2 ?? undefined, units: l.units,
      effectiveFrom: l.effectiveFrom ?? undefined, effectiveTo: l.effectiveTo ?? undefined,
    })),
  }));
}
/** Every live Test Method with a valid configured hold time — the rows of the
 *  Hold Time Target grid (Matrix · Test · Method · Hold Time). */
export async function getHoldTimeCatalog(): Promise<HoldTimeCatalogRow[]> {
  const res = await api.get('/api/v1/notification-rules/hold-time-catalog');
  return ((res.data?.data ?? []) as HoldTimeCatalogRow[]).map((r) => ({ ...r, holdHours: Number(r.holdHours) }));
}
/** One active user for the Recipient Group Users tab (live Users data source). */
export interface RecipientUser { userId: string; userName: string; name?: string | null; roles?: string | null; department?: string | null }
/** Every active user with roles + department, for the Members → Users tab. */
export async function getRecipientUsers(): Promise<RecipientUser[]> {
  const res = await api.get('/api/v1/notification-rules/recipient-users');
  return (res.data?.data ?? []) as RecipientUser[];
}
/** Distinct Role + Department pairs for the recipients picker (from live Users). */
export async function getRecipientRoles(): Promise<RecipientRole[]> {
  const res = await api.get('/api/v1/notification-rules/recipient-roles');
  return (res.data?.data ?? []) as RecipientRole[];
}
